import { supabase } from '@/lib/supabase';
import { invokeForgeWorker } from '@/lib/forge-worker-client';
import { parseAppSpec, type AppSpec } from '@shared/types/app-spec';

/** F6a: the stored description of an app (null = its current screens), its version and what could not be read. */
export interface StoredAppSpec {
  spec: AppSpec | null;
  version: number;
  issues: string[];
}

export const loadAppSpec = async (appId: string): Promise<StoredAppSpec> => {
  const { data, error } = await supabase.from('tool_apps').select('app_spec, app_spec_version').eq('id', appId).maybeSingle();
  if (error) throw new Error('La description de l’application n’a pas pu être chargée.');
  const row = data as { app_spec: unknown; app_spec_version: number } | null;
  return { ...parseAppSpec(row?.app_spec ?? null), version: row?.app_spec_version ?? 0 };
};

/** Creator or manager only (checked by the server); a stale version is refused, never overwritten. */
export const saveAppSpec = async (
  appId: string, expectedVersion: number, spec: AppSpec | null, source: 'manual' | 'chat' | 'forge' | 'restore' = 'manual', request: string | null = null,
): Promise<number> => {
  const { data, error } = await supabase.rpc('save_app_spec_f6', {
    p_tool_app_id: appId, p_expected_version: expectedVersion, p_spec: spec, p_source: source, p_request: request,
  });
  if (error) {
    throw new Error(error.code === '40001' ? 'L’application a été modifiée entre-temps : rechargez la page.'
      : error.code === '42501' ? 'Seuls le créateur et les managers modifient cette application.'
        : 'La description n’a pas pu être enregistrée.');
  }
  return data as number;
};

export interface AppSpecRevision {
  version: number;
  spec: AppSpec | null;
  source: 'manual' | 'chat' | 'forge' | 'restore';
  request: string | null;
  savedBy: string | null;
  createdAt: string;
}

/** The saved descriptions, newest first (creator and managers only, by RLS). */
export const loadAppSpecRevisions = async (appId: string): Promise<AppSpecRevision[]> => {
  const { data, error } = await supabase.from('tool_app_spec_revisions')
    .select('version, spec, source, request, saved_by, created_at').eq('tool_app_id', appId).order('version', { ascending: false });
  if (error) throw new Error('L’historique de l’application n’a pas pu être chargé.');
  return (data ?? []).map((row) => ({
    version: row.version as number, spec: parseAppSpec(row.spec).spec, source: row.source as AppSpecRevision['source'],
    request: (row.request as string | null) ?? null, savedBy: (row.saved_by as string | null) ?? null, createdAt: row.created_at as string,
  }));
};

export type AppSpecProposal =
  | { outcome: 'patch'; message: string; spec: AppSpec; changedIds: string[] }
  | { outcome: 'needs_new_data' | 'not_possible'; message: string; missing: string | null }
  | { outcome: 'invalid'; message: string; issues: string[] };

/** F6c: a change asked in words, prepared by the Forge (Opus); nothing is saved until the person applies it. */
export const proposeAppSpecChange = async (appId: string, request: string): Promise<{ proposal: AppSpecProposal; baseVersion: number }> =>
  await invokeForgeWorker({ action: 'app_spec_edit', appId, request }) as { proposal: AppSpecProposal; baseVersion: number };
